import { RectangleStackIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import { ChevronDownIcon } from '@heroicons/vue/24/solid';
import { Component, Prop, Watch, toNative, Vue } from '@natureos/vue-facing-decorator';
import { computed, ref } from 'vue';

/**
 * Module-level presence registry. Each mounted `<ToolDock>` registers
 * itself + its `collapsed` state here so surfaces that anchor to the
 * bottom of the viewport (e.g. `<Toasts>`) can lift themselves above the
 * dock without measuring the DOM.
 *
 * Consumers import the computed refs below. The registry itself is an
 * implementation detail of `<ToolDock>` and is not exported.
 */
interface OpenDock {
  id: number;
  collapsed: boolean;
}

const openDocks = ref<OpenDock[]>([]);
let nextDockId = 0;

/** True when at least one `<ToolDock>` is mounted (collapsed or not). */
export const anyToolDockOpen = computed(() => openDocks.value.length > 0);

/** True when at least one mounted `<ToolDock>` is not collapsed. */
export const anyToolDockExpanded = computed(() =>
  openDocks.value.some((d) => !d.collapsed),
);

/**
 * Contract every tool-internal layer manager satisfies for the
 * `<ToolDock>`'s automatic close-cleanup.
 *
 * **`deactivateAll()` semantics — both:**
 *   1. Remove every OL layer the manager has added to the map and
 *      reset internal tracking (so a subsequent `deactivate(key)` is
 *      a no-op).
 *   2. Abort any in-flight network work the manager initiated — the
 *      mosaic POST + tilejson GET pair on satellite, the BE stats
 *      fetches on the keyed-layer-owner family. Otherwise a user
 *      closing the dock mid-load leaves a doomed POST burning BE
 *      quota and (worse) writing a stale result back to the layer
 *      after the dock has closed.
 *
 * After a successful port the manager has already drained its
 * tracking via `releaseTracking`, so this method is a no-op for
 * ported layers — they stay sidebar-owned on the map exactly as the
 * port helper re-stamped them.
 */
export interface ToolLayerOwner {
  deactivateAll(): void;
}

/**
 * Bottom-docked panel chrome shared by the map's tool windows
 * (`<SatelliteTool>`, `<FireRiskTool>`, future Early Warning tools).
 *
 * Owns: the teleport-into-target wrapper, the collapse/close header bar,
 * the v-show on the body, and **closing tool-managed OL layers on
 * close** so individual tools no longer have to remember to deactivate
 * their layer managers in their per-tool close handlers (EUDR forgot
 * historically and leaked ghost layers; this contract makes that
 * mistake impossible to repeat). Each tool keeps its own
 * `isOpen` / `panelCollapsed` state and toggles them via the events;
 * the dock has no awareness of which tool it's hosting beyond the
 * `layer-owner` prop tools may pass to opt in to automatic cleanup.
 *
 * Each consumer mounts at most one `<ToolDock>` (gated by its own
 * `v-if="isOpen"`) and provides its content via the default slot. Place a
 * matching `<div :id="target">` somewhere in the page where the dock
 * should appear (typically below the map, above the layer-tab drawer).
 */
@Component({
  components: { ChevronDownIcon, RectangleStackIcon, XMarkIcon },
  // Required because <teleport> makes the component a fragment root —
  // without an `emits` declaration, parent `@toggleCollapse` / `@port`
  // / `@close` listeners are dropped as extraneous fallthrough attrs
  // (Vue logs a warning), and the parent never receives the events.
  emits: ['toggleCollapse', 'port', 'close'],
})
class ToolDock extends Vue {
  /** DOM id of the teleport target (without the `#`). */
  @Prop({ required: true })
  public readonly target!: string;

  /** Hides the body but keeps the header visible — the tool itself stays open. */
  @Prop({ required: true })
  public readonly collapsed!: boolean;

  /** Accessibility label for the header toolbar (e.g. "Satellite toolbox"). */
  @Prop({ required: true })
  public readonly ariaLabel!: string;

  /**
   * When true, the header shows a "Port to sidebar" button that emits
   * `port`. Tools whose layers have a sidebar counterpart (EUDR,
   * Feasibility) opt in so the user can keep the layers visible after
   * closing the tool. Tools with no sidebar counterpart (fire-risk's
   * GWIS WMS, which was deliberately stripped from the layer system)
   * leave it false.
   */
  @Prop({ required: false, default: false })
  public readonly canPort!: boolean;

  /**
   * One or more layer managers whose `deactivateAll()` is invoked
   * automatically when the user closes the dock. Lifts the
   * "remove-tool-layers-on-close" invariant into the dock so each
   * tool's `@close` handler can focus on store / state cleanup
   * (`isActive = false`, abort controllers, etc.) without forgetting
   * the OL-map side. EUDR's three-manager port use-case drove the
   * `LayerOwner[]` overload; single-manager tools pass the manager
   * directly.
   *
   * After port, the layer managers' `releaseTracking` flow already
   * cleared the entries they own, so the auto-deactivate here is a
   * no-op for ported layers — the layers stay sidebar-owned on the
   * map exactly as the port-helper re-stamped them.
   */
  @Prop({ required: false, default: () => [] })
  public readonly layerOwner!: ToolLayerOwner | ToolLayerOwner[];

  /**
   * Short description rendered as the first row of the dock body —
   * tells the user what the tool does + what the active surface
   * represents. Hybrid contract with the `#description` slot: when the
   * slot has content the dock renders that; otherwise it wraps this
   * string in a standard small-italic `<p>`. Tools with mode-dependent
   * copy (e.g. Satellite, where the description changes per sensor +
   * index) use the slot; simple tools pass a single string here.
   */
  @Prop({ required: false, default: '' })
  public readonly description!: string;

  /**
   * Local collapse state for the description section. Defaults to
   * open so the user reads the explanation on first encounter; clicking
   * the chevron tucks it away. Per-dock instance — not persisted
   * across reopens, since the explanation is short and re-reading it
   * is cheap.
   */
  public descriptionOpen: boolean = true;

  /**
   * Click handler for the close (`X`) button. Runs the auto-deactivate
   * pass for every owner the tool passed, then emits `close` so the
   * tool's own `@close` handler can do store / abort-controller /
   * etc. cleanup. A throw inside any owner's `deactivateAll` is
   * isolated to that owner — the remaining cleanups still run and
   * `close` still fires — so a buggy manager can't strand the dock
   * in an open state.
   */
  public onCloseClick(): void {
    const owners = Array.isArray(this.layerOwner) ? this.layerOwner : [this.layerOwner];
    for (const owner of owners) {
      if (!owner) {continue;}
      try {
        owner.deactivateAll();
      } catch (err) {
        console.error('[ToolDock] layer-owner cleanup failed on close:', err);
      }
    }
    this.$emit('close');
  }

  /**
   * Presence-registry id assigned on mount. Kept private — only the
   * registry interacts with it, not the template or consumers.
   */
  private dockId: number | null = null;

  public mounted(): void {
    this.dockId = nextDockId++;
    openDocks.value.push({ id: this.dockId, collapsed: this.collapsed });
  }

  public beforeUnmount(): void {
    if (this.dockId == null) {return;}
    openDocks.value = openDocks.value.filter((d) => d.id !== this.dockId);
    this.dockId = null;
  }

  @Watch('collapsed')
  private onCollapsedChange(): void {
    if (this.dockId == null) {return;}
    const dock = openDocks.value.find((d) => d.id === this.dockId);
    if (dock) {
      dock.collapsed = this.collapsed;
    }
  }
}

export default toNative(ToolDock);
export { default as ToolDock } from './tool-dock.component.vue';
